// Tours booked in the operator's system (chey.com.br), shared by the server, the ticket page and the admin.
// `boats` (boats or jeeps) are filled in this order; when none of them has room, a tour with `repasse` waits
// for the admin: accepting books the guest on the repasse product (another company), rejecting refunds the Pix.
// A tour without `boats` (Trindade, always a repasse) waits for the admin on every booking.
// `id`s and `name`s are chey's own (its /receptivo/api/vagas list); `label` is what the guest reads on the
// ticket ('' when there is nothing to tell apart).

export const CHEY_TOURS = {
  aquamarina: {
    slug: 'passeio-em-aquamarina-ll',
    // The only boat: the ticket's title already names it.
    boats: [{ id: 6, name: 'ESCUNA AQUAMARINA II', label: '' }],
    // The site sells the 11:00 departure (chey's default for the boat is another time).
    departure: '11:00',
    fixedDeparture: true,
    meetingPoint: 'Cais de Paraty',
    art: { boat: 'aquamarina-boat' },
  },
  mamangua: {
    slug: 'saco-de-mamangua-lancha-rapida',
    boats: [
      { id: 20, name: 'LANCHA VALERIANA I, MAMANGUÁ', label: 'Lancha Valeriana I' },
      { id: 44, name: 'VALERIANA 2, Mamanguá', label: 'Lancha Valeriana II' },
    ],
    repasse: { id: 27, name: 'REPASSE MAMANGUÁ', label: '' },
    // Each Valeriana leaves at its own time in chey; a repasse leaves at the tour page's time unless the admin
    // types another one when accepting it.
    departure: '10:30',
    meetingPoint: 'Cais de Turismo de Paraty',
    art: { boat: 'mamangua-boat' },
  },
  mataAtlantica: {
    slug: 'jeep-mata-atlantica',
    boats: [
      { id: 18, name: 'JEEP 1- CACHOEIRAS', label: '' },
      { id: 19, name: 'JEEP 2- CACHOEIRAS', label: '' },
      { id: 22, name: 'JEEP 3 CACHOEIRAS', label: '' },
      { id: 45, name: 'JEEP 4- CACHOEIRAS', label: '' },
    ],
    repasse: { id: 47, name: 'Jeep Repasse', label: '' },
    // Leaves at 11:00 (every voucher in chey says 11:00); the pick-up runs 10:20–11:00, or the guest boards at
    // the agency 40 minutes before.
    departure: '11:00',
    fixedDeparture: true,
    pickup: { from: '10:20', agencyLead: 40 },
    meetingPoint: '',
    art: { boat: 'mata-atlantica-jeep' },
  },
  trindade: {
    slug: 'jeep-mata-atlantica-15823',
    // Always a repasse: every booking waits for the admin, who books it on JEEP TRINDADE.
    boats: [],
    repasse: { id: 21, name: 'JEEP TRINDADE', label: '' },
    // Leaves at 11:00; pick-up 10:20–11:00, or the agency 20 minutes before.
    departure: '11:00',
    fixedDeparture: true,
    pickup: { from: '10:20', agencyLead: 20 },
    meetingPoint: '',
    art: { boat: 'trindade-jeep' },
  },
};

/** Where a guest who chooses not to be picked up boards the jeeps (the checkout fills it in with one tap). */
export const AGENCY_PICKUP = 'Agência Paraty Sirena Tours – Av. Roberto Silveira, 578, Paraty';

/** True when some tour of the list picks guests up (the checkout then asks where). */
export function needsPickup(items) {
  return (items || []).some((item) => Boolean(CHEY_TOURS[cheyTourKey(item?.slug)]?.pickup));
}

const minus = (time, minutes) => {
  const [h, m] = String(time).split(':').map(Number);
  const total = h * 60 + m - minutes;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * The pick-up of a tour that has one, for a guest's address (`place`; '' when not known yet): at their lodging
 * between `from` and the departure, or at the agency at `arrive` (`lead` minutes before). null for other tours.
 */
export function pickupFor(tour, place) {
  if (!tour?.pickup) return null;
  const text = String(place || '').trim();
  const atAgency = text === AGENCY_PICKUP;
  return { place: text, atAgency, from: tour.pickup.from, departure: tour.departure, lead: tour.pickup.agencyLead, arrive: minus(tour.departure, tour.pickup.agencyLead) };
}

/** The key of a chey tour ('aquamarina', 'mamangua', 'mataAtlantica', 'trindade') from a tour slug, or ''. */
export function cheyTourKey(slug) {
  const value = String(slug || '').toLowerCase();
  return Object.keys(CHEY_TOURS).find((key) => CHEY_TOURS[key].slug === value) || '';
}

/**
 * A paid website order books one line of chey's tours (its voucher, its review and its ticket follow that line): an
 * order with two of them, or one tour on two dates, is refused before paying, and the guest books them one by one.
 */
export function oneCheyLineRefusal(items) {
  const lines = (Array.isArray(items) ? items : []).filter((item) => cheyTourKey(item?.slug));
  if (lines.length <= 1) return null;
  return {
    code: 'one_chey_tour',
    error: 'A Escuna Aquamarina II, o Saco do Mamanguá e os passeios de jipe são reservados um por vez (um passeio e uma data por pagamento). Deixe só um deles no carrinho, pague e depois reserve o outro.',
  };
}

/** The tour a chey boat or jeep belongs to (each id is one tour's), or '' for one the site does not list. */
export function tourOfBoat(boatId) {
  const id = Number(boatId);
  return Object.keys(CHEY_TOURS).find((key) => [...CHEY_TOURS[key].boats, CHEY_TOURS[key].repasse].filter(Boolean).some((boat) => boat.id === id)) || '';
}

/** The line of a reservation booked in chey, with its tour: { key, tour, item }, or null. */
export function cheyLineOf(reservation) {
  for (const item of reservation?.items || []) {
    const key = cheyTourKey(item?.slug);
    if (key) return { key, tour: CHEY_TOURS[key], item };
  }
  return null;
}

/** "09:30 hrs" → "09:30", "10:30 a 11:00" or "10:30-11hr" → "10:30 – 11:00"; anything else as it came. */
export function cleanDeparture(value) {
  const times = [...String(value || '').matchAll(/(\d{1,2})(?::(\d{2})|h)/gi)].slice(0, 2)
    .map((match) => `${match[1].padStart(2, '0')}:${match[2] || '00'}`);
  return times.length ? times.join(' – ') : String(value || '').trim();
}
